import { type ProjectMeta } from "@tokenterminal/tt-analytics-api-types/dist/api/projects"
import {
  DataTable,
  type DatatableApi,
  type DataTableProps,
  type ColDef,
  type FirstDataRenderedEvent,
  type SetFilterModel,
  type NumberFilterModel,
  type TextFilterModel,
  type IMultiFilterModel,
  type ProcessCellForExportParams,
} from "@tokenterminal/ui/DataTable"
import { useAuth } from "context/AuthContext"
import {
  ACCESS_ACTIONS,
  ACCESS_COMPONENTS,
  useAccess,
} from "modules/auth/useAccess"
import { useFeatureSwitch } from "modules/featureswitch/FeatureSwitch"
import { useProjectsWithPlaceholdersMeta } from "modules/project/hooks/useMeta"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { gaOneOffEvent } from "utils/analytics"
import { createStableObject } from "utils/stableObjectKeys"
import {
  type ExportParams,
  type ExportType,
  useDataGridState,
} from "./DataGridContext"
import { type DataGridState, type MartPreset } from "./types"
import { makeExporter } from "./utils/makeExporter"
export * from "@tokenterminal/ui/DataTable"

export interface InternalProps {
  preset?: null | MartPreset
  withPaywall?: boolean
  suppressDownloads?: boolean
  initialFilter?: Record<
    string,
    SetFilterModel | NumberFilterModel | TextFilterModel | IMultiFilterModel
  >
  onStateChange?: (state: DataGridState) => void
}

const defaultGetRowId: DataTableProps<any>["getRowId"] = (params) => {
  return Object.entries(params.data)
    .filter(([_, value]) => Boolean(value))
    .map(([columnName, value]) =>
      typeof value === "object"
        ? Object.values(value!)
        : `${columnName}-${value}`
    )
    .join("_")
}

const processCellCallback = (params: ProcessCellForExportParams) => {
  if (params.value instanceof Date) {
    return params.value.toISOString()
  }

  if (params.column.getColDef().useValueFormatterForExport === false) {
    return params.value
  }

  return params.formatValue(params.value)
}

export function DataGrid<T extends Record<string, unknown>, C = {}>({
  withPaywall = true,
  onFirstDataRendered: onFirstDataRenderedCallback,
  api,
  data,
  datasource,
  columnDefs,
  getRowId = defaultGetRowId,
  suppressDownloads: martSuppressesDownloads,
  initialFilter,
  preset,
  onStateChange,
  ...props
}: DataTableProps<T, C> & InternalProps) {
  const [, setGridState] = useDataGridState<T>()
  const { user } = useAuth()
  const fallbackApiRef = useRef<DatatableApi>(null)
  const datagridApi = api || fallbackApiRef

  const dirtyListeners = useRef<{ listen: Function; cleanup: Function }>()
  const [currentData, setData] = useState<Array<T> | undefined>()
  const isExternalLinksEnabled = useFeatureSwitch("EXTERNAL_LINKS_ENABLED")
  const isDownloadEnabled = useFeatureSwitch("DOWNLOAD_ENABLED")
  const projects = useProjectsWithPlaceholdersMeta()
  const canDownload = useAccess(
    ACCESS_COMPONENTS.DATASETS,
    ACCESS_ACTIONS.DOWNLOAD
  )
  const onFirstDataRendered = useCallback(
    (params: FirstDataRenderedEvent<T, C>) => {
      const dirtyListener = (type: string) => {
        switch (type) {
          case "columnVisible":
          case "columnMoved":
          case "sortChanged":
          case "filterChanged":
          case "chartCreated":
          case "rowGroupOpened":
          case "columnValueChanged":
          case "columnRowGroupChanged":
          case "columnResized":
            if (onStateChange && datagridApi.current) {
              onStateChange(createStableObject(datagridApi.current.getState()))
            }

            // All dirty events
            setGridState((state) => ({ ...state, dirty: true }))
            break
          default:
            break
        }
      }

      setGridState((state) => ({
        ...state,
        getState: () => datagridApi.current?.getState(),
        downloadCsv: () =>
          params.api.exportDataAsCsv({
            processCellCallback,
          }),
        downloadExcel: () =>
          params.api.exportDataAsExcel({
            processCellCallback,
          }),
        createExporter: (type: ExportType, params: ExportParams) =>
          makeExporter(type, { ...params, processCellCallback }),
        getColDefs: () => params.api.getColumnDefs(),
      }))

      if (onStateChange && datagridApi.current) {
        onStateChange(createStableObject(datagridApi.current.getState()))
      }

      // Setup listeners and start listening to for the first time
      dirtyListeners.current = {
        cleanup: () => params.api.removeGlobalListener(dirtyListener),
        listen: () => params.api.addGlobalListener(dirtyListener),
      }
      dirtyListeners.current.listen()
      onFirstDataRenderedCallback?.(params)
    },
    // Using passed ref
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onFirstDataRenderedCallback, setGridState]
  )

  const projectBasedDataTypeColumnIds = useMemo(() => {
    const columnIds: Array<string> = []
    columnDefs?.forEach((columnDef: ColDef<T, any>) => {
      if (
        columnDef.field &&
        (columnDef.cellDataType === "project" ||
          columnDef.cellDataType === "chain" ||
          columnDef.cellDataType === "collection")
      ) {
        columnIds.push(columnDef.field)
      }
    })

    return columnIds
  }, [columnDefs])

  // project/chain columns have project logos, the dataset doesn't provide these so we
  // attach them as refData to the column which we can use in the cellRenderer
  useEffect(() => {
    if (projects.length && projectBasedDataTypeColumnIds.length) {
      const projectsCache: Record<string, ProjectMeta> = {}
      projects.forEach((project) => {
        if (project.name) {
          projectsCache[project.name] = project
        }
      })

      // TODO: Ugly fix until we get product meta for opBNB
      if (!projectsCache["opBNB"]) {
        // @ts-ignore
        projectsCache["opBNB"] = {
          name: "opBNB",
          logo: "/bsc/products/opbnb/logo.png",
        }
      }

      projectBasedDataTypeColumnIds.forEach((columnId) => {
        datagridApi.current?.updateRefData(
          columnId,
          projectsCache,
          (params) => {
            return params.value ?? "Unknown"
          }
        )
      })
    }
  }, [projects, datagridApi, projectBasedDataTypeColumnIds])

  useEffect(
    function applyPreset() {
      if (preset) {
        datagridApi.current?.applyState(preset.config)
      } else {
        ;(async () => {
          await datagridApi.current?.resetState()

          if (initialFilter && datagridApi.current) {
            datagridApi.current.applyState({
              columns: datagridApi.current.getState().columns,
              filters: initialFilter,
              expandedRows: [],
            })
          }

          if (data) {
            // If client side mode
            setData(data)
          }
        })()
      }
    },
    [data, datagridApi, initialFilter, preset]
  )

  // Downloading is a feature flagged pro feature, which can be suppressed per mart/dataset too
  const suppressDownloads =
    martSuppressesDownloads || !canDownload || !isDownloadEnabled

  return (
    <DataTable
      allowExternalLinks={isExternalLinksEnabled}
      suppressExcelExport={suppressDownloads}
      suppressCsvExport={suppressDownloads}
      data={currentData}
      api={datagridApi}
      onFirstDataRendered={onFirstDataRendered}
      datasource={datasource}
      columnDefs={columnDefs}
      getRowId={getRowId}
      onChartCreatedEvent={() => {
        gaOneOffEvent({
          event: "agChartCreated",
          user: user?.id ?? "",
        })
      }}
      {...props}
    />
  )
}
